import {
  Box,
  Button,
  Center,
  Flex,
  Radio,
  RadioGroup,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip
} from "@chakra-ui/react";
import api from "api/api";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { FaSave } from "react-icons/fa";

import { openModalError } from "components/modal-error";
import { Toast } from "components/toast";
import { WhatsAppSessionListProps } from "../types";
import { InputField } from "components/input-field";
import { makeEvent, useEventListener } from "services/events";
import { CustomModal } from "components/custom-modal";
import { InfoBalloon } from "components/info-balloon";
import { openModalConfirm } from "components/modal-confirm-new";
import { WarningIcon } from "components/vectors/warning-icon";
import { UseWebSocketProps } from "components/atendimentos-components/use-init-web-socket/types";
import { useApplicationContext } from "contexts/ApplicationContext";
import { RowEmpresaProps } from "components/atendimentos-components/attendance-filter/types";
import { DropdownOptionProps } from "components/dropdown";
import { InputBoxSearch } from "components/input-box-search";
import { isUserGerente } from "utils/is-gerente";
import { InputSearch } from "components/input-search";
import { validateSessionWhatsApp } from "./utils";

type AddWhatsAppServerModalParams = {
  serverId?: number;
};

type Type = WhatsAppSessionListProps["type"] | "";

export interface WhatsAppSessionProps {
  nome?: string;
  type?: Type;
  customerId?: number;
  novosAtendimentosUsersIds?: number[];
  associarNovosAtendimentos?: boolean;
  novosAtendimentosEquipeId?: number;
}

const intialValue: Partial<WhatsAppSessionProps> = {
  associarNovosAtendimentos: false,
  type: "DISPARO_CAMPANHA"
};

export function AddWhatsAppServerModal({
  setServers,
  servers,
  sendJsonMessage,
  reconnectWs
}: {
  servers: WhatsAppSessionListProps[];
  setServers: Dispatch<SetStateAction<WhatsAppSessionListProps[]>>;
  sendJsonMessage: UseWebSocketProps["sendJsonMessage"];
  reconnectWs: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  let [sessionData, setSessionData] =
    useState<Partial<WhatsAppSessionProps>>(intialValue);
  const [serverId, setServerId] = useState<number | null>(null);
  const [tipoDeAtribuicao, setTipoDeAtribuicao] = useState<
    "vendedores" | "equipes"
  >("vendedores");
  // Loadings
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  // Listas
  const [equipes, setEquipes] = useState<
    { name: string; value: number; usersIds: number[] }[]
  >([]);
  const [empresasList, setEmpresasList] = useState<DropdownOptionProps[]>([]);
  const [vendedores, setVendendores] = useState<DropdownOptionProps[]>([]);
  const [errors, setErrors] = useState<
    Partial<Record<keyof WhatsAppSessionProps, string>>
  >({});
  const { user } = useApplicationContext();
  const currentServer = servers.find((server) => server.id === serverId);
  const isUpdate = !!serverId;
  const inputRef = useRef<HTMLInputElement>(null);

  const isGerente = isUserGerente(user);

  const onOpen = ({ serverId }: AddWhatsAppServerModalParams) => {
    if (serverId) {
      const server = servers.find((server) => server.id === serverId);
      if (server) {
        onChangeForm(server.nome, "nome");
        onChangeForm(server.type, "type");
        onChangeForm(
          server.associarNovosAtendimentos,
          "associarNovosAtendimentos"
        );
        onChangeForm(
          server.novosAtendimentosUsersIds,
          "novosAtendimentosUsersIds"
        );
        onChangeForm(
          server.novosAtendimentosEquipeId,
          "novosAtendimentosEquipeId"
        );
        onChangeForm(server.customerId, "customerId");
        setServerId(serverId);
        if (server.novosAtendimentosUsersIds?.length) {
          setTipoDeAtribuicao("vendedores");
        }
        if (server.novosAtendimentosEquipeId) {
          setTipoDeAtribuicao("equipes");
        }
      }
    }
    setIsOpen(true);
  };

  const onClose = () => {
    setSessionData(intialValue);
    setIsOpen(false);
    setServerId(null);
    // setCustomerId(undefined);
  };

  const putSession = async (
    param: { remover_das_campanhas: boolean } | undefined,
    body: WhatsAppSessionProps
  ) => {
    return await api.put(`/whatsapp-sessions/${serverId}`, body, {
      params: param
    });
  };

  const postSession = async (body: WhatsAppSessionProps) => {
    return await api.post("/whatsapp-sessions", body);
  };

  const getEmpresas = async () => {
    setIsLoadingEmpresas(true);
    try {
      const { data } = await api.get<RowEmpresaProps[]>(`/customers`);
      setEmpresasList([
        ...data.map(({ id, name, codigoNova }) => {
          if (user.userData.type === "PARCEIRO_GERENTE_VAREJO") {
            return { name: name, value: id };
          }
          return { name: `${codigoNova} - ${name}`, value: id };
        })
      ]);
    } catch (e) {
      Toast({ title: "Erro ao carregar empresas", status: "error" });
    } finally {
      setIsLoadingEmpresas(false);
    }
  };

  const handleSubmit = async (param?: { remover_das_campanhas: boolean }) => {
    const { isValid, errors, body } = validateSessionWhatsApp(
      sessionData,
      tipoDeAtribuicao,
      user
    );

    if (!isValid) {
      setErrors(errors);
      console.log(`Errors =>`, errors);
      return;
    }

    setInternalLoading(true);
    try {
      const { data } = isUpdate
        ? await putSession(param, body)
        : await postSession(body);

      if (isUpdate) {
        setServers((servers) =>
          servers.map((server) => {
            if (server.id === data.id) return data;
            return server;
          })
        );
      } else {
        setServers([...servers, data]);
        sendJsonMessage({ action: "update-pm2" });
        reconnectWs();
      }

      Toast({
        title: isUpdate ? "Alterações Salvas" : "Servidor Criado",
        status: "success"
      });
    } catch (e: any) {
      const data = e?.response?.data;
      const errorMessage: string | undefined =
        typeof data?.message === "string" ? data.message : undefined;

      if (errorMessage === "exceeded_maximum_session") {
        openModalError({
          title: "Atenção",
          message:
            "Não é possivel criar novas sessões! Verifique a quantidade contratada."
        });
      } else if (
        errorMessage === "cannot_change_to_type_atendimento_belong_campanha"
      ) {
        openModalConfirm({
          title: (
            <Flex alignItems="center">
              <Box mr="8px" transform="translateY(-2px)">
                <WarningIcon color="secondary.700" w="20px" h="20px" />
              </Box>
              {"Erro"}
            </Flex>
          ),
          message: (
            <Box>
              <b>Não foi possível</b> alterar o tipo desta sessão para{" "}
              <b>Atendimento</b>, pois ela está{" "}
              <b style={{ color: "var(--chakra-colors-red-600)" }}>
                vinculada (s) a uma ou mais campanhas.
              </b>
              <br />
              <br />
              Deseja <b>desvincular</b> a sessão das{" "}
              <b>campanhas e continuar</b>?
            </Box>
          ),
          onConfirm: async () => {
            await handleSubmit({ remover_das_campanhas: true });
          }
        });
      } else {
        openModalError({
          title: "Erro",
          message: isUpdate
            ? "Não foi possível alterar a sessão!"
            : "Não foi possível criar a sessão!"
        });
      }
    } finally {
      setInternalLoading(false);
      onClose();
    }
  };

  const getUsers = async (customer_id: number) => {
    setIsLoadingUsers(true);
    try {
      const { data } = await api.get(
        `/usuarios/ai-disabled?customer_id=${customer_id}`
      );
      setVendendores(
        data.map((user: any) => ({ name: user.name, value: user.id }))
      );
    } catch (e) {
      Toast({ title: "Erro ao carregar usuários!", status: "error" });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const getEquipes = async (customer_id: number) => {
    try {
      const { data } = await api.get<
        {
          id: number;
          nome: string;
          enabled: boolean;
          customerId: number;
          usersIds: number[];
        }[]
      >(`/equipes`, { params: { customer_id, enabled: true } });
      setEquipes(
        data.map((item) => ({
          name: item.nome,
          value: item.id,
          usersIds: item.usersIds
        }))
      );
    } catch (e) {
      Toast({ title: "Erro ao carregar equipes!", status: "error" });
    } finally {
    }
  };

  const onChangeForm = (value: any, field: keyof WhatsAppSessionProps) => {
    sessionData = { ...sessionData, [field]: value };
    setSessionData(sessionData);
    setErrors((e) => {
      delete e[field];
      return { ...e };
    });
  };

  const onChangeMultiselect = (
    value: any,
    field: keyof WhatsAppSessionProps
  ) => {
    const currentEl = sessionData[field] as any[];

    if (currentEl?.length && currentEl != null) {
      const isInclude = currentEl.includes(value);
      if (isInclude) {
        onChangeForm(
          currentEl.filter((curr: any) => curr !== value),
          field
        );
      } else onChangeForm([...currentEl, value], field);
    } else onChangeForm([value], field);
  };

  useEffect(() => {
    setInternalLoading(false);
  }, [servers]);

  useEventListener("openModalAddWhatsAppServer", onOpen);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current && isOpen) {
        inputRef.current.focus();
      }
    }, 100);
  }, [isOpen]);

  useEffect(() => {
    if (isGerente) {
      getEmpresas();
    }
    if (!isGerente) {
      getUsers(user.userData.customerId!);
      getEquipes(user.userData.customerId!);
    }
  }, []);

  useEffect(() => {
    if (sessionData.customerId) {
      getUsers(sessionData.customerId);
      getEquipes(sessionData.customerId);
    }
  }, [sessionData.customerId]);

  const isDisabled = isGerente
    ? !sessionData.customerId ||
      !sessionData.nome ||
      !sessionData.type ||
      (sessionData.associarNovosAtendimentos
        ? tipoDeAtribuicao === "vendedores"
          ? !sessionData.novosAtendimentosUsersIds?.length
          : !sessionData.novosAtendimentosEquipeId
        : false)
    : !sessionData.nome ||
      !sessionData.type ||
      (sessionData.associarNovosAtendimentos
        ? tipoDeAtribuicao === "vendedores"
          ? !sessionData.novosAtendimentosUsersIds?.length
          : !sessionData.novosAtendimentosEquipeId
        : false);

  const modalFooter = (
    <>
      <Button
        size="sm"
        isLoading={internalLoading}
        loadingText="Criando Sessão"
        mt="10px"
        onClick={() => handleSubmit()}
        leftIcon={<FaSave />}
        isDisabled={isDisabled}
      >
        {isUpdate ? "Salvar Alterações" : "Criar Sessão"}
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={isUpdate ? "Editar Sessão WhatsApp" : "Criar Sessão WhatsApp"}
      modalFooter={modalFooter}
      size="xl"
      scroll="inside"
      isLoading={isLoadingUsers}
    >
      <Box w="100%">
        <Box mb="10px">
          <InputField
            ref={inputRef}
            title="Nome da sessão:"
            value={sessionData.nome}
            onChange={(e) => onChangeForm(e.target.value, "nome")}
            errorMessage={errors["nome"]}
          />
        </Box>
        {isGerente ? (
          <Box my="10px">
            <Text mb="8px">Empresa:</Text>
            <InputBoxSearch
              isLoading={isLoadingEmpresas}
              options={empresasList}
              onChange={(value) => {
                onChangeForm(value, "customerId");
              }}
              checkIsInclude={(option) =>
                sessionData.customerId === option.value
              }
              isDisabled={isLoadingEmpresas || isUpdate}
              openOnFocus
              resultLimit={Infinity}
              partialSearch
              value={
                empresasList.find(
                  (curr) => curr.value === sessionData.customerId
                )?.name || ""
              }
              inputBoxProps={{
                onFocus: (e) => {
                  e.currentTarget.textContent = "";
                },
                onBlur: (e) => {
                  e.currentTarget.textContent =
                    empresasList.find(
                      (curr) => curr.value === sessionData.customerId
                    )?.name || "";
                }
              }}
              errorMessage={errors["customerId"]}
            />
          </Box>
        ) : null}
        <Box>
          <Text mb="8px" fontWeight="medium">
            Tipo da sessão:
          </Text>
          <RadioGroup
            display="flex"
            fontWeight="medium"
            onChange={(value: any) => onChangeForm(value, "type")}
            value={sessionData.type}
          >
            <Radio
              mr="10px"
              value="DISPARO_CAMPANHA"
              isInvalid={!!errors["type"]}
            >
              <Text fontSize="14px">Disparo de Campanha</Text>
            </Radio>

            <Radio value="ATENDIMENTO" isInvalid={!!errors["type"]}>
              <Text fontSize="14px">Atendimento</Text>
            </Radio>
          </RadioGroup>
        </Box>

        <InfoBalloon mt="15px">
          <Box>
            As <b>Sessões de Atendimento</b> destinam-se exclusivamente ao{" "}
            <b>atendimento ao cliente.</b> Não é permitido selecionar uma{" "}
            <b>Sessão de Atendimento</b> para o envio de mensagens em campanhas,
            pois estão mais suscetíveis a <b>banimentos</b> ao serem usadas para
            essa finalidade.
          </Box>
        </InfoBalloon>

        <Box
          border="1px solid var(--chakra-colors-gray-200)"
          borderRadius="10px"
          w="100%"
          p="10px"
          mt="15px"
        >
          <Flex
            w="100%"
            mb="8px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex alignItems="center">
              <Text fontSize="15px" fontWeight="semibold">
                Atribuição de atendimentos
              </Text>
            </Flex>
            <Center>
              <Text fontSize="14px" fontWeight="medium" mr="5px">
                Habilitar
              </Text>
              <Tooltip
                label={
                  currentServer?.ativoVendeAI
                    ? "A IA está ativada nesta sessão. Para habilitar a atribuição de atendimentos, desative-a primeiro."
                    : undefined
                }
                bg="white"
                border="1px solid var(--chakra-colors-custom-gray)"
                color="prymary.500"
                boxShadow="none"
                borderRadius="10px"
              >
                <Box>
                  <Switch
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      onChangeForm(isChecked, "associarNovosAtendimentos");
                      if (!isChecked) {
                        onChangeForm(undefined, "novosAtendimentosUsersIds");
                        onChangeForm(undefined, "novosAtendimentosEquipeId");
                      }
                    }}
                    isChecked={sessionData.associarNovosAtendimentos}
                    isDisabled={
                      (isGerente && !sessionData.customerId) ||
                      currentServer?.ativoVendeAI
                    }
                  />
                </Box>
              </Tooltip>
            </Center>
          </Flex>
          <Text mb="8px" color="gray.500">
            Direcione automaticamente novos atendimentos desta sessão para um
            vendedor específico.
          </Text>
          <Flex mb="8px">
            <Button
              mr="8px"
              variant="outline"
              fontSize="13px"
              isActive={tipoDeAtribuicao === "vendedores"}
              onClick={() => {
                setTipoDeAtribuicao("vendedores");
                onChangeForm(undefined, "novosAtendimentosEquipeId");
              }}
              isDisabled={!sessionData.associarNovosAtendimentos}
            >
              Vendedores Individuais
            </Button>
            <Button
              variant="outline"
              fontSize="13px"
              isActive={tipoDeAtribuicao === "equipes"}
              onClick={() => {
                setTipoDeAtribuicao("equipes");
                onChangeForm(undefined, "novosAtendimentosUsersIds");
              }}
              isDisabled={!sessionData.associarNovosAtendimentos}
            >
              Equipe de vendas
            </Button>
          </Flex>
          {tipoDeAtribuicao === "vendedores" ? (
            <Box mb="8px">
              <Text fontSize="14px" fontWeight="medium" mb="8px">
                Vendedores:
              </Text>
              <Flex
                flexDir="column"
                borderRadius="10px"
                border="1px solid var(--chakra-colors-custom-gray)"
                w="100%"
                opacity={!sessionData.associarNovosAtendimentos ? 0.6 : 1}
              >
                <InputSearch
                  onChange={(optionValue) => {
                    onChangeMultiselect(
                      optionValue,
                      "novosAtendimentosUsersIds"
                    );
                  }}
                  searchKeys={["name", "value"]}
                  checkIsInclude={(option) =>
                    !!sessionData.novosAtendimentosUsersIds?.includes(
                      option.value
                    )
                  }
                  options={vendedores.filter(
                    (option) =>
                      !sessionData.novosAtendimentosUsersIds?.includes(
                        option.value
                      )
                  )}
                  partialSearch
                  openOnFocus
                  inputProps={{
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: sessionData.novosAtendimentosUsersIds?.length
                      ? "1px solid var(--chakra-colors-custom-gray)"
                      : "none"
                  }}
                  listContainerProps={{ maxH: "157px" }}
                  errorMessage={errors["novosAtendimentosUsersIds"]}
                  isDisabled={!sessionData.associarNovosAtendimentos}
                  isLoading={isLoadingUsers}
                />
                <Flex
                  px="10px"
                  py={
                    sessionData.novosAtendimentosUsersIds?.length
                      ? "10px"
                      : undefined
                  }
                  gap="10px"
                  flexWrap="wrap"
                >
                  {sessionData.novosAtendimentosUsersIds?.map(
                    (optionValue, index) => {
                      const parseValue = vendedores.find(
                        (option) => optionValue === option.value
                      );
                      return (
                        <Tag
                          key={optionValue}
                          variant="solid"
                          colorScheme={"blue"}
                        >
                          <TagLabel>{`${parseValue?.name}`}</TagLabel>
                          <TagCloseButton
                            onClick={() => {
                              onChangeMultiselect(
                                parseValue?.value,
                                "novosAtendimentosUsersIds"
                              );
                            }}
                          />
                        </Tag>
                      );
                    }
                  )}
                </Flex>
              </Flex>
            </Box>
          ) : (
            <Box mb="8px">
              <Text fontSize="14px" fontWeight="medium" mb="8px">
                Equipes:
              </Text>
              <InputSearch
                onChange={(value) => {
                  onChangeForm(value, "novosAtendimentosEquipeId");
                  onChangeForm(undefined, "novosAtendimentosUsersIds");
                }}
                options={equipes}
                value={
                  equipes?.find(
                    (option) =>
                      option.value === sessionData.novosAtendimentosEquipeId
                  )?.name
                }
                checkIsInclude={(option) =>
                  sessionData.novosAtendimentosEquipeId === option.value
                }
                openOnFocus
                partialSearch
                clearInput={false}
                inputProps={{
                  isDisabled: !sessionData.associarNovosAtendimentos
                }}
                listContainerProps={{ maxH: "157px" }}
                errorMessage={errors["novosAtendimentosEquipeId"]}
                isLoading={isLoadingUsers}
                // emptyMessage={"Nenhum vendedor encontrado"}
              />
            </Box>
          )}
          <Box
            mt="15px"
            borderRadius="10px"
            border="1px solid var(--chakra-colors-gray-200)"
            bg="var(--chakra-colors-gray-50)"
            p="10px"
          >
            <Text mb="8px" fontSize="15px" fontWeight={"semibold"}>
              Pré-visualização
            </Text>
            <Text fontSize="14px" color="gray.500">
              {tipoDeAtribuicao === "equipes"
                ? `Novos atendimentos serão atribuídos para a equipe: ${
                    equipes.find(
                      ({ value }) =>
                        value === sessionData.novosAtendimentosEquipeId
                    )?.name || "(não selecionado)"
                  }. Os atendimentos serão distribuídos entre os ${
                    equipes.find(
                      ({ value }) =>
                        value === sessionData.novosAtendimentosEquipeId
                    )?.usersIds?.length || ""
                  } vendedores da equipe. Reatendimentos permanecem com o mesmo vendedor quando possível.`
                : `Novos atendimentos serão atribuídos para: ${
                    vendedores
                      .filter((option) =>
                        sessionData.novosAtendimentosUsersIds?.includes(
                          option.value
                        )
                      )
                      .map(({ name }) => name)
                      .join(", ") || "(não selecionado)"
                  }. Apenas clientes novos serão atribuídos ao(s) vendedor(es).
              Reatendimentos permanecem com o mesmo vendedor quando possível.`}
            </Text>
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
}

export const openModalAddWhatsAppServer = (
  props: AddWhatsAppServerModalParams = {}
) => makeEvent("openModalAddWhatsAppServer", props);
